import type { Component, Container, ExternalSystem, Risk, RiskCategory } from "./arch-parser";

export type OverlayCategory =
  | "cost_impact"
  | "scale_performance"
  | "security"
  | "compliance"
  | "data_readiness"
  | "data_connectivity";

// === Structured Risk ↔ Overlay category mapping =============================
// Canonical (v0.3) risks carry a RiskCategory directly; the overlay system
// works in OverlayCategory. Map between them so callers don't have to.
// `operability`, `vendor-lock-in`, and `other` don't have an overlay surface
// today — they return null and fall through to "uncategorized" in the risks
// sheet and "no match" in the overlay highlighter.

const RISK_TO_OVERLAY: Record<RiskCategory, OverlayCategory | null> = {
  cost: "cost_impact",
  scalability: "scale_performance",
  security: "security",
  compliance: "compliance",
  "data-readiness": "data_readiness",
  "data-connectivity": "data_connectivity",
  operability: null,
  "vendor-lock-in": null,
  other: null,
};

export function riskCategoryToOverlay(c: RiskCategory): OverlayCategory | null {
  return RISK_TO_OVERLAY[c] ?? null;
}

const OVERLAY_TO_RISK: Record<OverlayCategory, RiskCategory> = {
  cost_impact: "cost",
  scale_performance: "scalability",
  security: "security",
  compliance: "compliance",
  data_readiness: "data-readiness",
  data_connectivity: "data-connectivity",
};

export function overlayToRiskCategory(o: OverlayCategory): RiskCategory {
  return OVERLAY_TO_RISK[o];
}

export type OverlayMode = OverlayCategory | "none";

export const OVERLAY_CATEGORIES: ReadonlyArray<OverlayCategory> = [
  "cost_impact",
  "scale_performance",
  "security",
  "compliance",
  "data_readiness",
  "data_connectivity",
];

export const OVERLAY_LABELS: Record<OverlayCategory, string> = {
  cost_impact: "Cost",
  scale_performance: "Scale",
  security: "Security",
  compliance: "Compliance",
  data_readiness: "Data readiness",
  data_connectivity: "Data connectivity",
};

const KEYWORDS: Record<OverlayCategory, readonly RegExp[]> = {
  cost_impact: [
    /\bcost(s|ly)?\b/i,
    /\$/,
    /\bbudget\b/i,
    /\bspend(ing)?\b/i,
    /\bexpensive\b/i,
    /\bpricing\b/i,
    /\bfees?\b/i,
    /\bbill(ing)?\b/i,
    /\blicen[sc]ing?\b/i,
    /\bquota\b/i,
    /\begress\b/i,
  ],
  scale_performance: [
    /\bscal(e|ing|ability)\b/i,
    /\bperf(ormance)?\b/i,
    /\bthroughput\b/i,
    /\blatenc(y|ies)\b/i,
    /\bslow\b/i,
    /\bload\b/i,
    /\bconcurren(t|cy)\b/i,
    /\bcapacity\b/i,
    /\bbottleneck\b/i,
    /\b(r|q)ps\b/i,
    /\bp(95|99|999)\b/i,
    /\bmemory\b/i,
    /\bcpu\b/i,
    /\boom\b/i,
    /\bsingle (instance|point|node)\b/i,
    /\btimeout\b/i,
  ],
  security: [
    /\bsecurity\b/i,
    /\bauth(n|z|orization|entication)?\b/i,
    /\btoken(s)?\b/i,
    /\bsecret(s)?\b/i,
    /\bpassword(s)?\b/i,
    /\bencrypt(ion|ed)?\b/i,
    /\bdecrypt(ion|ed)?\b/i,
    /\bvuln(erab(le|ility))?\b/i,
    /\bexploit\b/i,
    /\bxss\b/i,
    /\bcsrf\b/i,
    /\bsqli?\b/i,
    /\binjection\b/i,
    /\bleak(ed|s)?\b/i,
    /\bexposure\b/i,
    /\bcredential(s)?\b/i,
    /\bkey rotation\b/i,
  ],
  compliance: [
    /\bcompliance\b/i,
    /\bpci( ?dss)?\b/i,
    /\bgdpr\b/i,
    /\bhipaa\b/i,
    /\bsoc ?2\b/i,
    /\bsox\b/i,
    /\bccpa\b/i,
    /\baudit(s|ing)?\b/i,
    /\bregulat(ion|ory)\b/i,
    /\blegal\b/i,
    /\bferpa\b/i,
    /\bretention\b/i,
    /\bresidency\b/i,
    /\bsso\b/i,
  ],
  data_readiness: [
    /\bdata quality\b/i,
    /\bmissing data\b/i,
    /\bdirty data\b/i,
    /\bclean(s|ing|sed)?\b/i,
    /\bgap(s)?\b/i,
    /\binconsisten(t|cy)\b/i,
    /\bschema drift\b/i,
    /\bvalidation\b/i,
    /\blineage\b/i,
    /\bcompleteness\b/i,
    /\baccuracy\b/i,
    /\bfreshness\b/i,
    /\bstaleness\b/i,
    /\bdedup(e|lication)?\b/i,
    /\bbackfill\b/i,
  ],
  data_connectivity: [
    /\bintegrat(e|ion|ions|ed)\b/i,
    /\bsync(ing|ed)?\b/i,
    /\betl\b/i,
    /\belt\b/i,
    /\bpipeline\b/i,
    /\bconnector(s)?\b/i,
    /\bwebhook(s)?\b/i,
    /\bthird[- ]party\b/i,
    /\bexternal dependenc(y|ies)\b/i,
    /\bingestion\b/i,
    /\bdownstream\b/i,
    /\bupstream\b/i,
    /\bapi dependenc(y|ies)\b/i,
  ],
};

const PREFIX = /^\s*([a-z_]+)\s*:\s*(.*)$/i;

function explicitPrefix(statement: string): OverlayCategory | null {
  const m = statement.match(PREFIX);
  if (!m) return null;
  const head = (m[1] ?? "").toLowerCase().replace(/[\s-]/g, "_");
  const aliases: Record<string, OverlayCategory> = {
    cost: "cost_impact",
    cost_impact: "cost_impact",
    scale: "scale_performance",
    scaling: "scale_performance",
    performance: "scale_performance",
    perf: "scale_performance",
    scale_performance: "scale_performance",
    security: "security",
    sec: "security",
    compliance: "compliance",
    legal: "compliance",
    data_readiness: "data_readiness",
    readiness: "data_readiness",
    data_quality: "data_readiness",
    data_connectivity: "data_connectivity",
    connectivity: "data_connectivity",
    integration: "data_connectivity",
  };
  return aliases[head] ?? null;
}

export function categorizeRisk(statement: string): OverlayCategory | null {
  if (!statement) return null;
  const explicit = explicitPrefix(statement);
  if (explicit) return explicit;
  for (const cat of OVERLAY_CATEGORIES) {
    for (const re of KEYWORDS[cat]) {
      if (re.test(statement)) return cat;
    }
  }
  return null;
}

function anyStructuredRiskMatches(risks: readonly Risk[], overlay: OverlayCategory): boolean {
  for (const r of risks) {
    if (riskCategoryToOverlay(r.category) === overlay) return true;
  }
  return false;
}

export function containerMatchesOverlay(
  container: Container,
  overlay: OverlayCategory,
  connectsToExternals: ReadonlySet<string>,
): boolean {
  if (anyStructuredRiskMatches(container.risks ?? [], overlay)) return true;
  if (overlay === "cost_impact" && container.cost !== undefined && container.cost !== false) {
    return true;
  }
  if (overlay === "data_connectivity" && connectsToExternals.size > 0) return true;
  return false;
}

export function componentMatchesOverlay(
  component: Component,
  overlay: OverlayCategory,
  connectsToExternals: ReadonlySet<string>,
): boolean {
  if (anyStructuredRiskMatches(component.risks ?? [], overlay)) return true;
  if (overlay === "cost_impact" && component.cost !== undefined && component.cost !== false) {
    return true;
  }
  if (overlay === "data_connectivity" && connectsToExternals.size > 0) return true;
  return false;
}

export function externalSystemMatchesOverlay(
  system: ExternalSystem,
  overlay: OverlayCategory,
): boolean {
  if (anyStructuredRiskMatches(system.risks ?? [], overlay)) return true;
  if (overlay === "cost_impact" && system.cost !== undefined && system.cost !== false) {
    return true;
  }
  // Every external system is a data-connectivity surface by definition.
  if (overlay === "data_connectivity") return true;
  return false;
}

const RING_CLASSES: Record<OverlayCategory, string> = {
  cost_impact: "ring-4 ring-amber-400/70",
  scale_performance: "ring-4 ring-orange-400/70",
  security: "ring-4 ring-red-400/70",
  compliance: "ring-4 ring-blue-400/70",
  data_readiness: "ring-4 ring-purple-400/70",
  data_connectivity: "ring-4 ring-teal-400/70",
};

export function overlayRingClass(overlay: OverlayCategory): string {
  return RING_CLASSES[overlay];
}
